// [목적] 컬렉션 상세 + 아이템 무한스크롤 TanStack Query 훅
// [이유] FEED-03 — CollectionPage 실데이터 연결. useCollection(id) 로 상세 조회,
//         useCollectionItems(id) 로 cursor 무한스크롤 아이템 목록 제공
// [사이드 이팩트] 403/404 ApiError → error 상태로 보존(throwOnError=false). 페이지가 직접 ErrorBoundary 로 throw 결정.
//                 useCollectionItems 는 useInfiniteQuery — nextCursor null 이면 마지막 페이지
// [수정 시 고려사항] private 컬렉션 비큐레이터 접근 시 BE 가 404 반환(정보 누설 방지 R-9).
//                   throwOnError=true 시 테스트 환경에서 미처리 Promise rejection 으로 vitest 실패.
//                   페이지에서 error 상태를 읽고 ErrorBoundary 또는 인라인 메시지 렌더 분기

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../api/queryKeys.js';
import { getById, listItems, type CollectionItemPage } from '../api/collection.client.js';
import type { CollectionDetail } from '@gangwoncanvas/types';

// ─── useCollection ────────────────────────────────────────────────────────────

/**
 * [목적] GET /collections/:id 컬렉션 상세 조회
 * [이유] FEED-03 CollectionPage 커버·큐레이터 정보 렌더링
 * [사이드 이팩트] 403/404 → error 상태로 노출 (throwOnError=false). 페이지가 인라인 메시지 또는 throw 결정
 * [수정 시 고려사항] id 가 undefined 이면 enabled=false 로 요청 차단
 */
export function useCollection(id: string | undefined): {
  collection: CollectionDetail | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.collection.detail(id ?? ''),
    queryFn: () => getById(id!),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 3, // 3분
    throwOnError: false,
  });

  return { collection: data, isLoading, error };
}

// ─── useCollectionItems ───────────────────────────────────────────────────────

const ITEMS_PER_PAGE = 20;

/**
 * [목적] GET /collections/:id/items cursor 무한스크롤 훅
 * [이유] FEED-03 CollectionPage 아이템 목록 — IntersectionObserver 로 "더 보기" 트리거
 * [사이드 이팩트] nextCursor=null 이면 hasNextPage=false. fetchNextPage 로 다음 페이지 로드
 * [수정 시 고려사항] getNextPageParam 이 null 반환 시 TanStack Query 가 hasNextPage=false 처리
 */
export function useCollectionItems(id: string | undefined): {
  pages: CollectionItemPage[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
} {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: queryKeys.collection.items(id ?? ''),
    queryFn: ({ pageParam }) =>
      listItems(id!, {
        cursor: pageParam as string | undefined,
        limit: ITEMS_PER_PAGE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: CollectionItemPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

  return {
    pages: data?.pages ?? [],
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLoading,
  };
}
